/**
 * "O que fazer agora" da Central: insights derivados dos numeros de TODAS
 * as empresas, ordenados por urgencia.
 *
 * Decisoes nao obvias:
 *  1. Cada insight e uma FUNCAO PURA que devolve `null` quando o dado nao o
 *     sustenta. Nada e inventado para preencher a lista: com tudo saudavel a
 *     tela mostra um so bloco dizendo isso.
 *  2. Todo texto carrega o numero que o justifica (quantas empresas, quanto,
 *     de que para que). Insight sem numero e opiniao.
 *  3. A prioridade e um inteiro fixo por tipo (0 = mais urgente): primeiro o
 *     que perde dinheiro hoje (margem negativa, cobranca sem regra), depois
 *     o que ameaca a receita (queda, concentracao), por fim o que otimiza.
 *  4. A ACAO e tipada: ou navega para uma rota, ou reconfigura a tabela
 *     desta pagina (filtro + ordem) e rola ate ela, ou so rola ate uma
 *     secao. A pagina executa; o insight nao sabe de React.
 *  5. Nenhum insight sugere agir ANTES do fato (lembrar de renovar, avisar
 *     antes do vencimento). O motor so entra depois do abandono ou da
 *     expiracao; e dai que vem a comissao (decisao do dono, 24/09/2026).
 */

import type { BadgeTone, IconName } from '../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps } from '../../lib/format';
import { caiuEmPontos, diasDesde, participacaoBps, porUnidade, variar } from './comparar';
import type { EstadoDaTabela } from './tabela';
import type { CompanyRow, CostChannel, PlatformOverview } from './types';
import { COMPANY_STATUS_LABEL, COST_CHANNEL_ORDER, COST_CHANNEL_SHORT } from './vocabulario';

export type AcaoDoInsight =
  | { readonly kind: 'rota'; readonly rotulo: string; readonly rota: string }
  | { readonly kind: 'tabela'; readonly rotulo: string; readonly estado: Partial<EstadoDaTabela> }
  | { readonly kind: 'ancora'; readonly rotulo: string; readonly id: string };

export interface InsightDaCentral {
  readonly id: string;
  /** 0 = mais urgente. */
  readonly prioridade: number;
  readonly tone: BadgeTone;
  readonly icone: IconName;
  readonly titulo: string;
  readonly texto: string;
  readonly acao: AcaoDoInsight | null;
}

const MAXIMO_DE_INSIGHTS = 5;
/** Margem sobre a comissao abaixo disto e "apertada". */
const MARGEM_APERTADA_BPS = 5_000;
/** Uma empresa com mais que isto da comissao e concentracao de risco. */
const CONCENTRACAO_BPS = 5_000;
/** Queda de recuperado por empresa que merece olhar. */
const QUEDA_POR_EMPRESA_BPS = 2_000;
/** Queda da taxa media da plataforma, em pontos-base. */
const QUEDA_DA_TAXA_BPS = 200;
/** Implantacao parada ha mais que isto vira insight. */
const DIAS_DE_IMPLANTACAO = 3;

function nomes(rows: readonly CompanyRow[], maximo = 2): string {
  const lista = rows.slice(0, maximo).map((r) => r.companyName);
  const resto = rows.length - lista.length;
  return resto > 0 ? `${lista.join(', ')} e mais ${formatCount(resto)}` : lista.join(' e ');
}

function plural(n: number, um: string, varios: string): string {
  return n === 1 ? um : varios;
}

function cadastroDe(companyId: string): string {
  return `/admin/empresas/${encodeURIComponent(companyId)}`;
}

type Gerador = (overview: PlatformOverview) => InsightDaCentral | null;

const margemNegativa: Gerador = (o) => {
  // Custo nao medido (null) nunca vira margem negativa: sem numero, sem alarme.
  const rows = o.companies.filter((r) => r.status === 'ativa' && r.marginCents !== null && r.marginCents < 0);
  if (rows.length === 0) {
    return null;
  }
  const custo = rows.reduce((acc, r) => acc + (r.costCents ?? 0), 0);
  const comissao = rows.reduce((acc, r) => acc + r.commissionCents, 0);
  return {
    id: 'margem-negativa',
    prioridade: 0,
    tone: 'critical',
    icone: 'moeda',
    titulo: `${formatCount(rows.length)} ${plural(rows.length, 'empresa custa', 'empresas custam')} mais do que ${plural(rows.length, 'rende', 'rendem')} no período`,
    texto: `${nomes(rows)}: ${formatBRLFromCents(custo)} de custos contra ${formatBRLFromCents(comissao)} de comissão. Custo fixo (instância, infra) continua correndo mesmo sem venda; confira volume e tarifa antes de faturar.`,
    acao: { kind: 'tabela', rotulo: 'Ver na tabela', estado: { filtro: 'margem_negativa', ordem: { coluna: 'margem', desc: false } } },
  };
};

const cobrancaADefinir: Gerador = (o) => {
  const rows = o.companies.filter((r) => r.billingModel === 'a_definir' && r.commissionCents > 0);
  if (rows.length === 0) {
    return null;
  }
  const comissao = rows.reduce((acc, r) => acc + r.commissionCents, 0);
  return {
    id: 'cobranca-a-definir',
    prioridade: 1,
    tone: 'warning',
    icone: 'comissao',
    titulo: `${formatBRLFromCents(comissao)} de comissão sem modelo de cobrança definido`,
    texto: `${nomes(rows)} ${plural(rows.length, 'gerou', 'geraram')} comissão no período com a cobrança "a definir" (contrato ou automático ainda não decidido). Até decidir, esse valor não vira fatura.`,
    acao: { kind: 'rota', rotulo: 'Ver comissões', rota: '/admin/comissoes' },
  };
};

const implantacaoParada: Gerador = (o) => {
  const rows = o.companies
    .filter((r) => r.status === 'onboarding' && !r.webhookConfigured)
    .map((r) => ({ row: r, dias: diasDesde(r.createdAt, o.capturedAt) }))
    .filter((x) => x.dias >= DIAS_DE_IMPLANTACAO)
    .sort((a, b) => b.dias - a.dias);
  const primeira = rows[0];
  if (primeira === undefined) {
    return null;
  }
  return {
    id: 'implantacao-parada',
    prioridade: 2,
    tone: 'warning',
    icone: 'chave',
    titulo: `${primeira.row.companyName} está há ${formatCount(primeira.dias)} dias em implantação sem webhook`,
    texto:
      rows.length === 1
        ? 'Sem a URL configurada no provedor, nenhum abandono chega ao motor: zero recuperação e zero comissão. Uma implantação concluída é uma empresa a mais rendendo.'
        : `${formatCount(rows.length)} empresas em implantação sem webhook. Sem a URL no provedor, nenhum abandono chega ao motor: zero recuperação e zero comissão.`,
    acao: { kind: 'rota', rotulo: 'Abrir cadastro', rota: cadastroDe(primeira.row.companyId) },
  };
};

const quedaPorEmpresa: Gerador = (o) => {
  // Uma entrada por empresa ativa que caiu 20% ou mais, com a variacao ja
  // calculada; a maior queda ABSOLUTA (mais negativa) vira o titulo.
  const quedas = o.companies.flatMap((r) => {
    if (r.status !== 'ativa' || r.previous === null || r.previous.recoveredAmountCents === 0) {
      return [];
    }
    const v = variar(r.recoveredAmountCents, r.previous.recoveredAmountCents);
    if (v.direcao !== 'queda' || v.relativaBps === null || -v.relativaBps < QUEDA_POR_EMPRESA_BPS) {
      return [];
    }
    return [{ row: r, anteriorCents: r.previous.recoveredAmountCents, relativaBps: v.relativaBps, absoluta: v.absoluta }];
  });
  const pior = [...quedas].sort((a, b) => a.absoluta - b.absoluta)[0];
  if (pior === undefined) {
    return null;
  }
  return {
    id: 'queda-por-empresa',
    prioridade: 3,
    tone: 'warning',
    icone: 'grafico',
    titulo: `Recuperado caiu ${formatPercentFromBps(Math.abs(pior.relativaBps))} em ${pior.row.companyName}`,
    texto: `De ${formatBRLFromCents(pior.anteriorCents)} para ${formatBRLFromCents(pior.row.recoveredAmountCents)} contra o período anterior${
      quedas.length > 1 ? `; ${formatCount(quedas.length)} empresas caíram 20% ou mais` : ''
    }. Queda forte costuma ser integração muda ou canal caído, não o mercado.`,
    acao: {
      kind: 'tabela',
      rotulo: 'Ver na tabela',
      estado: { filtro: 'todas', busca: quedas.length === 1 ? pior.row.companyName : '', ordem: { coluna: 'recuperado', desc: true } },
    },
  };
};

const concentracao: Gerador = (o) => {
  const comComissao = o.companies.filter((r) => r.commissionCents > 0);
  const maior = [...comComissao].sort((a, b) => b.commissionCents - a.commissionCents)[0];
  if (maior === undefined || comComissao.length < 2) {
    return null;
  }
  const parte = participacaoBps(maior.commissionCents, o.totals.commissionCents);
  if (parte === null || parte < CONCENTRACAO_BPS) {
    return null;
  }
  return {
    id: 'concentracao',
    prioridade: 4,
    tone: 'info',
    icone: 'empresa',
    titulo: `${maior.companyName} concentra ${formatPercentFromBps(parte)} da comissão do período`,
    texto: `Se ela pausar, o faturamento cai quase pela metade. Concluir as implantações em fila e trazer empresas novas dilui o risco — cada uma paga só sobre o que recuperamos depois do abandono ou do vencimento.`,
    acao: { kind: 'rota', rotulo: 'Ver empresas', rota: '/admin/empresas' },
  };
};

const taxaCaiu: Gerador = (o) => {
  const anterior = o.previousTotals;
  if (anterior === null || anterior.abandonedCount === 0 || o.totals.abandonedCount === 0) {
    return null;
  }
  if (!caiuEmPontos(o.totals.recoveryRateBps, anterior.recoveryRateBps, QUEDA_DA_TAXA_BPS)) {
    return null;
  }
  return {
    id: 'taxa-caiu',
    prioridade: 5,
    tone: 'warning',
    icone: 'grafico',
    titulo: `Taxa média de recuperação caiu de ${formatPercentFromBps(anterior.recoveryRateBps)} para ${formatPercentFromBps(o.totals.recoveryRateBps)}`,
    texto: `Com ${formatCount(o.totals.abandonedCount)} entradas no período, cada ponto de taxa vale ${formatCount(Math.round(o.totals.abandonedCount / 100))} vendas. Compare a taxa por empresa: quem caiu costuma ter um canal parado.`,
    acao: { kind: 'tabela', rotulo: 'Ordenar por taxa', estado: { filtro: 'ativa', ordem: { coluna: 'taxa', desc: false } } },
  };
};

const margemApertada: Gerador = (o) => {
  const rows = o.companies.filter((r) => r.status === 'ativa' && r.marginCents !== null && r.marginCents >= 0 && r.marginBps !== null && r.marginBps < MARGEM_APERTADA_BPS);
  if (rows.length === 0) {
    return null;
  }
  return {
    id: 'margem-apertada',
    prioridade: 6,
    tone: 'warning',
    icone: 'moeda',
    titulo: `Custo consome mais da metade da comissão em ${formatCount(rows.length)} ${plural(rows.length, 'empresa', 'empresas')}`,
    texto: `${nomes(rows)}: margem abaixo de 50% sobre a comissão. Volume baixo dilui mal o custo fixo (instância e infra); voz é o canal mais caro por lead.`,
    acao: { kind: 'tabela', rotulo: 'Ordenar por margem', estado: { filtro: 'ativa', ordem: { coluna: 'margem', desc: false } } },
  };
};

const estornos: Gerador = (o) => {
  if (o.totals.reversedCount === 0) {
    return null;
  }
  return {
    id: 'estornos',
    prioridade: 7,
    tone: 'info',
    icone: 'alerta',
    titulo: `${formatCount(o.totals.reversedCount)} ${plural(o.totals.reversedCount, 'conversão revertida', 'conversões revertidas')} no período (${formatBRLFromCents(o.totals.reversedAmountCents)})`,
    texto: 'Chargeback reverte a comissão; reembolso não, salvo política diferente no cadastro da empresa. O que saiu da apuração já está fora da comissão geral acima.',
    acao: { kind: 'rota', rotulo: 'Ver comissões', rota: '/admin/comissoes' },
  };
};

const canalMaisCaro: Gerador = (o) => {
  const custo = o.totals.costByChannel;
  if (custo === null || custo.totalCents === 0 || o.totals.recoveredCount === 0) {
    return null;
  }
  const porCanal: Readonly<Record<CostChannel, number>> = { voice: custo.voiceCents, whatsapp: custo.whatsappCents, email: custo.emailCents, infra: custo.infraCents };
  const canal = [...COST_CHANNEL_ORDER].sort((a, b) => porCanal[b] - porCanal[a])[0];
  if (canal === undefined) {
    return null;
  }
  const parte = participacaoBps(porCanal[canal], custo.totalCents);
  const porVenda = porUnidade(custo.totalCents, o.totals.recoveredCount);
  if (parte === null || porVenda === null) {
    return null;
  }
  return {
    id: 'canal-mais-caro',
    prioridade: 8,
    tone: 'neutral',
    icone: 'grafico',
    titulo: `${COST_CHANNEL_SHORT[canal]} responde por ${formatPercentFromBps(parte)} dos custos; cada venda recuperada custa ${formatBRLFromCents(porVenda)}`,
    texto: `Custo total de ${formatBRLFromCents(custo.totalCents)} para ${formatCount(o.totals.recoveredCount)} vendas. Tarifas de exemplo na prévia: quando as faturas reais entrarem, este número é o primeiro a conferir.`,
    acao: { kind: 'ancora', rotulo: 'Ver custos por canal', id: 'custos-por-canal' },
  };
};

const pausadas: Gerador = (o) => {
  const rows = o.companies.filter((r) => r.status === 'pausada');
  if (rows.length === 0) {
    return null;
  }
  return {
    id: 'pausadas',
    prioridade: 9,
    tone: 'neutral',
    icone: 'pausa',
    titulo: `${formatCount(rows.length)} ${plural(rows.length, 'empresa pausada', 'empresas pausadas')} sem gerar comissão`,
    texto: `${nomes(rows)} ${plural(rows.length, 'está', 'estão')} com status "${COMPANY_STATUS_LABEL.pausada}". Enquanto pausada, nenhum abandono é trabalhado e nada é cobrado.`,
    acao: { kind: 'tabela', rotulo: 'Ver pausadas', estado: { filtro: 'pausada', ordem: { coluna: 'ultimoEvento', desc: true } } },
  };
};

const GERADORES: readonly Gerador[] = [margemNegativa, cobrancaADefinir, implantacaoParada, quedaPorEmpresa, concentracao, taxaCaiu, margemApertada, estornos, canalMaisCaro, pausadas];

export function gerarInsightsDaCentral(overview: PlatformOverview): readonly InsightDaCentral[] {
  return GERADORES.map((g) => g(overview))
    .filter((i): i is InsightDaCentral => i !== null)
    .sort((a, b) => a.prioridade - b.prioridade)
    .slice(0, MAXIMO_DE_INSIGHTS);
}
